import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Printer, Loader2, Download, QrCode, Share2, Wand2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import MissingPersonFlyer from './MissingPersonFlyer';
import ShareButtons from './ShareButtons';

export default function FlyerModal({ isOpen, onClose, caseData, ageProgressions = [] }) {
  const flyerRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    phone: '911 or Local Police',
    tip_line: '',
  });
  const [qrOptions, setQrOptions] = useState({
    showQrCode: true,
    showTipQr: true,
  });
  const [photoOptions, setPhotoOptions] = useState({
    showOriginal: true,
    showAgeProgression: false,
    selectedProgressionIdx: 0,
  });

  // Get the base URL for QR codes
  const getBaseUrl = () => {
    // Use the live site URL, not localhost
    return window.location.origin;
  };

  const getCaseUrl = () => `${getBaseUrl()}/case/${caseData?.id}`;
  const getTipUrl = () => `${getBaseUrl()}/tip/${caseData?.id}`;

  // Generate QR code as data URL for printing
  const generateQrDataUrl = (url, size = 80) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    // We'll use an SVG approach converted to data URL
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
        <rect width="100%" height="100%" fill="white"/>
        <foreignObject width="100%" height="100%">
          <div xmlns="http://www.w3.org/1999/xhtml">
            ${document.getElementById(`qr-${url.includes('submit-tip') ? 'tip' : 'case'}`)?.innerHTML || ''}
          </div>
        </foreignObject>
      </svg>
    `;
    
    return url;
  };

  // Pre-load image when modal opens
  useEffect(() => {
    if (isOpen && caseData) {
      setImageReady(false);
      const imgSrc = caseData.photo_base64 
        ? (caseData.photo_base64.startsWith('data:') 
            ? caseData.photo_base64 
            : `data:image/jpeg;base64,${caseData.photo_base64}`)
        : caseData.photo_url;
      
      if (imgSrc) {
        const img = new Image();
        img.onload = () => setImageReady(true);
        img.onerror = () => setImageReady(true); // Continue even if image fails
        img.src = imgSrc;
      } else {
        setImageReady(true);
      }
    }
  }, [isOpen, caseData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const handlePrint = () => {
    if (!caseData) return;
    
    setIsPrinting(true);

    // Get image source
    const imgSrc = caseData.photo_base64 
      ? (caseData.photo_base64.startsWith('data:') 
          ? caseData.photo_base64 
          : `data:image/jpeg;base64,${caseData.photo_base64}`)
      : caseData.photo_url || null;

    // Get age progression image if enabled
    const showAgeProg = photoOptions.showAgeProgression && ageProgressions && ageProgressions.length > 0;
    const selectedProgression = showAgeProg ? ageProgressions[photoOptions.selectedProgressionIdx || 0] : null;
    const ageProgImgSrc = selectedProgression?.image_url || selectedProgression?.image_base64 || null;

    // Build the HTML for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Missing Person Flyer - ${caseData.name || 'Unknown'}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          @page {
            size: letter portrait;
            margin: 0;
          }
          @media print {
            html, body {
              width: 8.5in;
              height: 11in;
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          body {
            font-family: Arial, Helvetica, sans-serif;
            width: 8.5in;
            min-height: 11in;
            padding: 0.4in;
            background: white;
            color: black;
          }
          .header {
            background-color: #dc2626 !important;
            color: white !important;
            padding: 20px;
            text-align: center;
            margin-bottom: 16px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .header h1 {
            font-size: 52px;
            font-weight: bold;
            letter-spacing: 12px;
            text-transform: uppercase;
          }
          .main-content {
            display: flex;
            gap: 20px;
            margin-bottom: 16px;
          }
          .photo-section {
            flex: 0 0 260px;
          }
          .photo {
            width: 260px;
            height: 320px;
            object-fit: cover;
            border: 3px solid #1f2937;
            display: block;
          }
          .photo-placeholder {
            width: 260px;
            height: 320px;
            background-color: #d1d5db;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid #1f2937;
            font-size: 72px;
            font-weight: bold;
            color: #6b7280;
          }
          .age-progression-section {
            margin-top: 12px;
            text-align: center;
          }
          .age-progression-photo {
            width: 200px;
            height: 200px;
            object-fit: cover;
            border: 3px solid #7c3aed;
            border-radius: 8px;
            display: block;
            margin: 0 auto;
          }
          .age-progression-label {
            font-size: 11px;
            font-weight: bold;
            color: #7c3aed;
            margin-top: 6px;
            text-transform: uppercase;
          }
          .age-progression-note {
            font-size: 10px;
            color: #6b7280;
            margin-top: 2px;
          }
          .info-section {
            flex: 1;
          }
          .info-section h2 {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 12px;
            color: #111827;
            border-bottom: 2px solid #111827;
            padding-bottom: 8px;
          }
          .info-table {
            width: 100%;
            font-size: 16px;
            border-collapse: collapse;
          }
          .info-table td {
            padding: 4px 0;
          }
          .info-table td:first-child {
            font-weight: bold;
            width: 100px;
            padding-right: 8px;
          }
          .last-seen {
            background-color: #fef3c7 !important;
            border: 2px solid #d97706;
            padding: 12px 16px;
            margin-bottom: 12px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .last-seen h3 {
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 8px;
            color: #92400e;
          }
          .last-seen p {
            font-size: 16px;
            margin: 4px 0;
          }
          .description {
            margin-bottom: 12px;
          }
          .description h3 {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 8px;
            color: #111827;
            border-bottom: 1px solid #d1d5db;
            padding-bottom: 4px;
          }
          .description p {
            font-size: 14px;
            line-height: 1.4;
            color: #374151;
            margin: 4px 0;
          }
          .contact {
            background-color: #1f2937 !important;
            color: white !important;
            padding: 16px;
            margin-bottom: 12px;
            text-align: center;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .contact h3 {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .contact .phone {
            font-size: 28px;
            font-weight: bold;
            margin: 8px 0;
          }
          .contact .tip-line {
            font-size: 18px;
            margin: 4px 0;
          }
          .contact .ncmec {
            font-size: 14px;
            margin-top: 8px;
            opacity: 0.9;
          }
          .footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top: 1px solid #d1d5db;
            padding-top: 8px;
            font-size: 12px;
            color: #6b7280;
          }
          .case-url {
            text-align: center;
            margin-top: 8px;
            font-size: 12px;
            color: #6b7280;
          }
          .qr-section {
            display: flex;
            justify-content: center;
            gap: 40px;
            margin: 16px 0;
            padding: 16px;
            background-color: #f8fafc !important;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .qr-item {
            text-align: center;
          }
          .qr-code {
            background: white;
            padding: 8px;
            border: 2px solid #1f2937;
            border-radius: 4px;
            display: inline-block;
          }
          .qr-label {
            font-size: 11px;
            font-weight: bold;
            margin-top: 6px;
            color: #1f2937;
          }
          .qr-sublabel {
            font-size: 10px;
            color: #6b7280;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>MISSING</h1>
        </div>
        
        <div class="main-content">
          <div class="photo-section">
            ${imgSrc 
              ? `<img class="photo" src="${imgSrc}" alt="${caseData.name || 'Missing Person'}" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" /><div class="photo-placeholder" style="display:none;">${getInitials(caseData.name)}</div>`
              : `<div class="photo-placeholder">${getInitials(caseData.name)}</div>`
            }
            ${showAgeProg && ageProgImgSrc ? `
            <div class="age-progression-section">
              <img class="age-progression-photo" src="${ageProgImgSrc}" alt="Age Progression" />
              <div class="age-progression-label">🔮 Age Progression</div>
              <div class="age-progression-note">+${selectedProgression?.years_progressed || 'N'} years (AI-generated)</div>
            </div>
            ` : ''}
          </div>
          <div class="info-section">
            <h2>${caseData.name || 'Unknown'}</h2>
            <table class="info-table">
              ${caseData.age ? `<tr><td>Age:</td><td>${caseData.age} years old</td></tr>` : ''}
              ${caseData.gender ? `<tr><td>Gender:</td><td>${caseData.gender}</td></tr>` : ''}
              ${caseData.race ? `<tr><td>Race:</td><td>${caseData.race}</td></tr>` : ''}
              ${(caseData.height || caseData.weight) ? `<tr><td>Build:</td><td>${[caseData.height, caseData.weight].filter(Boolean).join(', ')}</td></tr>` : ''}
              ${caseData.hair_color ? `<tr><td>Hair:</td><td>${caseData.hair_color}</td></tr>` : ''}
              ${caseData.eye_color ? `<tr><td>Eyes:</td><td>${caseData.eye_color}</td></tr>` : ''}
            </table>
          </div>
        </div>
        
        <div class="last-seen">
          <h3>📍 LAST SEEN</h3>
          <p><strong>Location:</strong> ${caseData.last_seen_location || caseData.state || 'Unknown'}</p>
          <p><strong>Date:</strong> ${formatDate(caseData.last_seen_date || caseData.missing_since)}</p>
        </div>
        
        ${(caseData.description || caseData.distinguishing_features || caseData.physical_features) ? `
        <div class="description">
          <h3>DESCRIPTION</h3>
          ${caseData.description ? `<p>${caseData.description}</p>` : ''}
          ${(caseData.distinguishing_features || caseData.physical_features) ? `<p><strong>Distinguishing Features:</strong> ${caseData.distinguishing_features || caseData.physical_features}</p>` : ''}
        </div>
        ` : ''}
        
        <div class="contact">
          <h3>IF YOU HAVE ANY INFORMATION, PLEASE CONTACT:</h3>
          <div class="phone">${contactInfo.phone || '911 or Local Police'}</div>
          ${contactInfo.tip_line ? `<div class="tip-line">Tip Line: ${contactInfo.tip_line}</div>` : ''}
          <div class="ncmec">National Center for Missing & Exploited Children: <strong>1-800-THE-LOST (1-800-843-5678)</strong></div>
        </div>
        
        ${(qrOptions.showQrCode || qrOptions.showTipQr) ? `
        <div class="qr-section">
          ${qrOptions.showQrCode ? `
          <div class="qr-item">
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(getCaseUrl())}" width="80" height="80" alt="Case QR Code" />
            </div>
            <div class="qr-label">📱 SCAN FOR DETAILS</div>
            <div class="qr-sublabel">View full case info</div>
          </div>
          ` : ''}
          ${qrOptions.showTipQr ? `
          <div class="qr-item">
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(getTipUrl())}" width="80" height="80" alt="Submit Tip QR Code" />
            </div>
            <div class="qr-label">💡 SUBMIT A TIP</div>
            <div class="qr-sublabel">Anonymous tips accepted</div>
          </div>
          ` : ''}
        </div>
        ` : ''}
        
        <div class="footer">
          <div>
            ${caseData.source_name ? `Source: ${caseData.source_name}` : ''}
            ${caseData.external_id ? ` | Case #: ${caseData.external_id.replace('namus_', '')}` : ''}
          </div>
          <div>Generated by LinkTheMissing.com</div>
        </div>
        
        <script>
          // Wait for QR codes and image to load then print
          window.onload = function() {
            setTimeout(function() {
              window.print();
              // Close after print dialog closes
              window.onafterprint = function() {
                window.close();
              };
            }, 1000);
          };
        </script>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank', 'width=850,height=1100');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    } else {
      alert('Please allow popups to print the flyer');
    }

    setIsPrinting(false);
  };

  if (!caseData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5" />
            Generate Missing Person Flyer
          </DialogTitle>
          <DialogDescription>
            Customize and print this flyer to help spread awareness
          </DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="phone" className="text-sm font-medium">
                Contact Phone Number
              </Label>
              <Input
                id="phone"
                placeholder="911 or Local Police"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Primary number for tips
              </p>
            </div>

            <div>
              <Label htmlFor="tip_line" className="text-sm font-medium">
                Additional Tip Line (Optional)
              </Label>
              <Input
                id="tip_line"
                placeholder="e.g., Crime Stoppers number"
                value={contactInfo.tip_line}
                onChange={(e) => setContactInfo({ ...contactInfo, tip_line: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Age Progression Option */}
            {ageProgressions && ageProgressions.length > 0 && (
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="w-4 h-4 text-purple-500" />
                  <Label className="text-sm font-medium">AI Age Progression</Label>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="age-prog" className="text-sm font-medium cursor-pointer">
                        Include Age Progression
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Show AI-generated age progression image
                      </p>
                    </div>
                    <Switch
                      id="age-prog"
                      checked={photoOptions.showAgeProgression}
                      onCheckedChange={(checked) => setPhotoOptions({ ...photoOptions, showAgeProgression: checked })}
                    />
                  </div>
                  
                  {photoOptions.showAgeProgression && ageProgressions.length > 1 && (
                    <div className="pl-2">
                      <Label className="text-xs text-muted-foreground mb-2 block">Select age progression:</Label>
                      <div className="flex gap-2 flex-wrap">
                        {ageProgressions.map((prog, idx) => (
                          <button
                            key={idx}
                            onClick={() => setPhotoOptions({ ...photoOptions, selectedProgressionIdx: idx })}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              photoOptions.selectedProgressionIdx === idx 
                                ? 'bg-purple-100 border-purple-500 text-purple-700' 
                                : 'bg-gray-50 border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            +{prog.years_progressed || (idx + 1) * 5} years
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {photoOptions.showAgeProgression && (
                  <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-md">
                    <p className="text-xs text-muted-foreground text-center">
                      🔮 AI-generated image shows how {caseData?.name?.split(' ')[0] || 'this person'} may look today
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* QR Code Options */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <QrCode className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">QR Code Options</Label>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="qr-case" className="text-sm font-medium cursor-pointer">
                      Case Details QR
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Links to full case information
                    </p>
                  </div>
                  <Switch
                    id="qr-case"
                    checked={qrOptions.showQrCode}
                    onCheckedChange={(checked) => setQrOptions({ ...qrOptions, showQrCode: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="qr-tip" className="text-sm font-medium cursor-pointer">
                      Submit Tip QR
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Quick link for anonymous tips
                    </p>
                  </div>
                  <Switch
                    id="qr-tip"
                    checked={qrOptions.showTipQr}
                    onCheckedChange={(checked) => setQrOptions({ ...qrOptions, showTipQr: checked })}
                  />
                </div>
              </div>
              
              {(qrOptions.showQrCode || qrOptions.showTipQr) && (
                <div className="mt-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
                  <p className="text-xs text-muted-foreground text-center">
                    📱 QR codes allow anyone with a smartphone to instantly access case details or submit tips
                  </p>
                </div>
              )}
            </div>

            <div className="pt-4 space-y-3">
              <Button
                onClick={handlePrint}
                disabled={isPrinting || !imageReady}
                className="w-full bg-safety-orange-500 hover:bg-safety-orange-600 gap-2"
                size="lg"
              >
                {isPrinting || !imageReady ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                {!imageReady ? 'Loading...' : isPrinting ? 'Preparing...' : 'Print Flyer'}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                A new window will open with your flyer ready to print
              </p>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium text-sm mb-2">Distribution Tips:</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Post in high-traffic areas</li>
                <li>• Share with local businesses</li>
                <li>• Distribute at community centers</li>
                <li>• Post on community bulletin boards</li>
                <li>• Share on social media groups</li>
              </ul>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium text-sm mb-2">Print Settings:</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Paper size: Letter (8.5" x 11")</li>
                <li>• Orientation: Portrait</li>
                <li>• Enable "Background graphics"</li>
                <li>• Set margins to "None" or "Minimum"</li>
              </ul>
            </div>

            {/* Social Share Section */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <Share2 className="w-4 h-4 text-muted-foreground" />
                <h4 className="font-medium text-sm">Share on Social Media</h4>
              </div>
              <ShareButtons 
                caseData={caseData} 
                caseId={caseData?.id}
                showLabel={false}
              />
            </div>
          </div>

          {/* Flyer Preview */}
          <div className="lg:col-span-2 border rounded-lg overflow-hidden bg-gray-100">
            <div className="text-center text-sm text-muted-foreground py-2 bg-gray-200">
              Flyer Preview (8.5" × 11")
            </div>
            <div 
              className="overflow-auto p-4"
              style={{ maxHeight: '70vh' }}
            >
              <div 
                className="bg-white shadow-lg mx-auto origin-top"
                style={{ 
                  transform: 'scale(0.55)', 
                  transformOrigin: 'top center',
                  width: '8.5in',
                  marginBottom: '-400px'
                }}
              >
                <MissingPersonFlyer
                  ref={flyerRef}
                  caseData={caseData}
                  contactInfo={contactInfo}
                  qrOptions={qrOptions}
                  photoOptions={photoOptions}
                  ageProgressions={ageProgressions}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
